import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  students as studentsApi, systemUsers as usersApi, academicYears, sundayGrades, sundayClasses,
  sundayTeachers, sundayAttendance, sundayOfferings, sundayExams, sundayExamMarks
} from '../../api/endpoints';
import { getList } from '../../api/client';
import { getStoredUser, isTeacherLogin, TEACHER_ROLE } from '../../utils/auth';
import {
  useFetchList, usePagination, useNotification, useSearch, useDateRangeFilter,
  useOpenRecordFromLink, fetchRecord, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SubTabs, Pagination, Notification, PromptDialog,
  FormLayout, Input, Select, Badge, Button, SearchBar, DateRangeFilter, PrintButton, Modal
} from '../../components/ui';
import type { Exam, WeeklyRegister, SundayOfferingRow, SundayGrade, AcademicClass } from '../../types';
import useSundaySchoolData from './useSundaySchoolData';
import type { SsRow } from './useSundaySchoolData';
import GradeBrowser from './GradeBrowser';
import { TablePanel } from '../../components/ui';
import type { Enrolment, EnrolledStudent } from './GradeBrowser';
import {
  nextRecordId, voidStamp, markProblem, sectionLabel, nextSectionLetters, lastSunday, isSunday, shiftSunday,
  sumOfferings,
  examAppliesTo, examSections, marksFor, marksEntered,
  passMarkFor, resultOf, tallyResults, DEFAULT_PASS_PCT
} from './registers';
import SundaySchoolSummary from './SundaySchoolSummary';
import { studentsWithoutSection } from './analytics';

// Every entity here is database-backed. Students come straight from
// /api/students; the other tables are loaded and written through
// useSundaySchoolData, which also stitches the weekly register together from
// the separate attendance and offering tables.
//
// Four tabs — Summary first, as on every module, then by how often each is used:
//   📊 Summary  — any time; opens first (a teacher still lands on Sunday)
//   📅 Sunday   — every week: pick the Sunday, mark each section, note the offering
//   🎓 Classes  — once a year: grades & sections, teachers, years
//   📝 Exams    — a few times a year
//
// A teacher's login (role "Sunday School Teacher", linked to Teacher rows by
// username) sees only their own sections: no Classes tab, no exam scheduling,
// no enrolment. The server enforces the same rule; here it is only shown.
//
// The whole screen is scoped to one academic year at a time (the picker in the
// header, shown once there is more than one year). Sections belong to a year,
// so a second year would otherwise put two "Grade 3 – A"s in every dropdown.

const EMPTY_YEAR = { name: '', startDate: '', endDate: '' };
const EMPTY_TEACHER = { name: '', phone: '', email: '', classId: '', username: '' };
const EMPTY_LOGIN = { username: '', password: '' };

/**
 * The same exam name on the same date, for the same year and grade, is a
 * re-entry rather than a new exam (CHM-131). Two *different* exams on one day
 * stay allowed — a school often sits two papers in a morning.
 */
const sameExam = (a: { name?: string; subject?: string; date?: string; academicYearId?: string; gradeId?: string },
                  b: { name?: string; subject?: string; date?: string; academicYearId?: string; gradeId?: string }) =>
  String(a.name || '').trim().toLowerCase() === String(b.name || '').trim().toLowerCase()
  && String(a.subject || '').trim().toLowerCase() === String(b.subject || '').trim().toLowerCase()
  && String(a.date || '') === String(b.date || '')
  && String(a.academicYearId || '') === String(b.academicYearId || '')
  && String(a.gradeId || '') === String(b.gradeId || '');

/** "Mary K. Thomas" -> "mary.k.thomas" — a starting username, editable. */
const suggestUsername = (name: string) =>
  String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.|\.$/g, '');
const EMPTY_STUDENT = { name: '', dob: '', parentName: '', phone: '', classId: '' };
// `studentId` blank = the section's whole-Sunday total; set = one child's gift (CHM-082).
const EMPTY_WEEKLY = { date: '', classId: '', studentId: '', offering: '' };
/** One line of the create-exams list. gradeId blank = every grade in the year. */
interface ExamRow { name: string; subject: string; date: string; totalMarks: string; passMarks: string; passTouched?: boolean; gradeId: string }
const EMPTY_EXAM_ROW: ExamRow = { name: '', subject: '', date: '', totalMarks: '100', passMarks: '40', gradeId: '' };
const EMPTY_EXAM_EDIT = { name: '', subject: '', date: '', totalMarks: '', passMarks: '', gradeId: '', academicYearId: '', classId: '' };
/** "Quarterly · Bible Knowledge" — an exam as the pickers and headings name it. */
const examTitle = (x: { name?: string; subject?: string }) =>
  [x.name, x.subject].map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
/** The pass mark that follows a total until the user types their own. */
const defaultPass = (total: string) => String(Math.round(((Number(total) || 0) * DEFAULT_PASS_PCT) / 100));

/** Every year, for the header picker. */
const ALL_YEARS = '';

/** What is about to be deleted, and which write does it. */
interface DeleteTarget {
  kind: 'register' | 'offering' | 'student' | 'grade' | 'section' | 'exam';
  rawId: number;
  label: string;
}

const SundaySchool = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  // Who is signed in decides how much of the screen exists.
  const me = getStoredUser();
  const teacherLogin = isTeacherLogin(me);

  // A teacher's week is marking the register, so they still land on Sunday.
  const [activeTab, setActiveTab] = useState(teacherLogin ? 'sunday' : 'summary');
  const [sundaySubTab, setSundaySubTab] = useState('mark');
  const [classesSubTab, setClassesSubTab] = useState('grades');
  const [examSubTab, setExamSubTab] = useState(teacherLogin ? 'marks' : 'create');
  const [showDeleted, setShowDeleted] = useState(false);
  const [yearsOpen, setYearsOpen] = useState(false);
  // History shows attendance or offerings, never both stacked.
  const [historyView, setHistoryView] = useState<'attendance' | 'offerings'>('attendance');
  // Which table (if any) currently owns the print stylesheet.

  const { notification, notify, dismiss } = useNotification();

  // --- Backend-backed ---
  const { data: rawStudents, refetch } = useFetchList((p, o) => studentsApi.list(p, o));
  const students = useMemo(
    () => rawStudents
      .filter((s) => s.status === 'ACTIVE')
      .map((s) => ({ ...s, id: s.studentId }))
      .sort((a, b) => String(b.id).localeCompare(String(a.id))),
    [rawStudents]
  );

  // --- The other tables, loaded and written together ---
  const { loading, years, grades, classes, teachers, weekly, exams, marks } = useSundaySchoolData(notify);

  const [yearForm, setYearForm] = useState(EMPTY_YEAR);
  const [teacherForm, setTeacherForm] = useState(EMPTY_TEACHER);
  const [showPassword, setShowPassword] = useState(false);
  // "Create login" for one teacher: which row, and the username/password typed.
  const [loginFor, setLoginFor] = useState<{ rawId: number; name: string } | null>(null);
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN);
  const [savingLogin, setSavingLogin] = useState(false);
  const [studentForm, setStudentForm] = useState(EMPTY_STUDENT);
  const [studentFormOpen, setStudentFormOpen] = useState(false);
  const [weeklyForm, setWeeklyForm] = useState(EMPTY_WEEKLY);
  const [offeringForm, setOfferingForm] = useState(EMPTY_WEEKLY);
  const [offeringFormOpen, setOfferingFormOpen] = useState(false);
  // Exams are created several at a time (Half Yearly + Annual...) for one year.
  const [examRows, setExamRows] = useState<ExamRow[]>([{ ...EMPTY_EXAM_ROW }]);
  const [examYear, setExamYear] = useState('');
  const [examFormOpen, setExamFormOpen] = useState(false);
  const [savingExams, setSavingExams] = useState(false);
  const [examEdit, setExamEdit] = useState(EMPTY_EXAM_EDIT);
  const [savingStudent, setSavingStudent] = useState(false);
  // Which created row is being edited, if any (CHM-076 — there was no edit).
  // One at a time, and only one tab is active, so a single slot is enough.
  const [editing, setEditing] = useState<{ kind: string; rawId: number } | null>(null);
  const editKind = (kind: string) => (editing?.kind === kind ? editing.rawId : null);
  const cancelEdit = () => setEditing(null);
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);

  // The Sunday being worked on. Starts at the most recent Sunday.
  const [sundayDate, setSundayDate] = useState(lastSunday());
  const [attendanceMap, setAttendanceMap] = useState<Record<string, boolean>>({});
  const [selectedExamId, setSelectedExamId] = useState('');
  // The section whose marks are open for entry, and what has been typed.
  const [marksSectionId, setMarksSectionId] = useState('');
  const [marksMap, setMarksMap] = useState({});
  const [reportSectionId, setReportSectionId] = useState('');
  /** The student whose report card is open on screen. */
  const [cardStudentId, setCardStudentId] = useState<string | null>(null);
  const [resultFilter, setResultFilter] = useState<'all' | 'failed' | 'none'>('all');

  const activeYear = years.items.find((y) => y.status === 'Active');
  const yearNameOf = (yearId) => years.items.find((y) => y.id === yearId)?.name || '-';

  // --- Year scope ------------------------------------------------------------
  // Defaults to the active year once the years have loaded; after that it is
  // the user's to change, so a refetch does not snap it back.
  const [yearScope, setYearScope] = useState<string>(ALL_YEARS);
  const scopeTouched = useRef(false);
  useEffect(() => {
    if (!scopeTouched.current && activeYear?.id) setYearScope(activeYear.id);
  }, [activeYear?.id]);
  const chooseScope = (value: string) => { scopeTouched.current = true; setYearScope(value); };
  const scopeName = yearScope ? yearNameOf(yearScope) : 'All years';

  // A teacher's sections: every Teacher row carrying their login username.
  const mySectionIds = useMemo(() => {
    if (!teacherLogin) return null;
    const username = String(me?.username || '').toLowerCase();
    return new Set(teachers.items
      .filter((x) => String(x.username || '').toLowerCase() === username && username)
      .map((x) => x.classId));
  }, [teacherLogin, me?.username, teachers.items]);

  const scopedClasses = useMemo(
    () => classes.items.filter((c) => (!yearScope || c.academicYearId === yearScope)
      && (!mySectionIds || mySectionIds.has(c.id))),
    [classes.items, yearScope, mySectionIds]
  );
  const scopedClassIds = useMemo(() => new Set(scopedClasses.map((c) => c.id)), [scopedClasses]);
  const inScope = useCallback((row: { classId?: string }) => scopedClassIds.has(row.classId), [scopedClassIds]);

  const scopedStudents = useMemo(() => students.filter(inScope), [students, inScope]);
  const scopedTeachers = useMemo(() => teachers.items.filter(inScope), [teachers.items, inScope]);
  // An exam is in scope when any section in scope sits it — a teacher sees
  // the school-wide exams because their section does.
  const scopedExams = useMemo(
    () => exams.items
      .filter((x) => scopedClasses.some((c) => examAppliesTo(x, c)))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [exams.items, scopedClasses]
  );
  const scopedWeekly = useMemo(() => weekly.items.filter(inScope), [weekly.items, inScope]);
  const scopedDeletedWeekly = useMemo(() => weekly.voidedItems.filter(inScope), [weekly.voidedItems, inScope]);
  const scopedOfferings = useMemo(() => weekly.offerings.filter(inScope), [weekly.offerings, inScope]);
  const scopedDeletedOfferings = useMemo(() => weekly.voidedOfferings.filter(inScope), [weekly.voidedOfferings, inScope]);

  // A section is known everywhere as "Grade 1 – A"; a class saved before
  // grades existed keeps its own name. With every year showing, the year
  // rides along too.
  const gradeOf = (c: SsRow<AcademicClass> | undefined) => (c?.gradeId ? grades.items.find((g) => g.id === c.gradeId) : undefined);
  const classNameOf = (classId) => {
    const c = classes.items.find((x) => x.id === classId);
    return c ? sectionLabel(c, gradeOf(c)) : 'Unassigned';
  };
  const sortedSections = useMemo(
    () => [...scopedClasses].sort((a, b) => classNameOf(a.id).localeCompare(classNameOf(b.id), undefined, { numeric: true })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedClasses, grades.items]
  );
  const classOptions = sortedSections.map((c) => ({
    value: c.id,
    label: yearScope ? sectionLabel(c, gradeOf(c)) : `${sectionLabel(c, gradeOf(c))} (${yearNameOf(c.academicYearId)})`
  }));
  const studentNameOf = (studentId) => students.find((s) => s.id === studentId)?.name || '-';
  const noClasses = scopedClasses.length === 0;

  // --- Lists: search, date filters, pages ------------------------------------
  const teacherSearch = useSearch(scopedTeachers, ['name', 'phone', 'email', (x) => classNameOf(x.classId)]);

  const weeklyRows = showDeleted ? scopedDeletedWeekly : scopedWeekly;
  const weeklyRange = useDateRangeFilter(weeklyRows);
  const weeklyPage = usePagination(weeklyRange.filtered);

  // The offerings view lists the raw rows (each section total and each
  // per-student gift separately), filtered to those carrying money (CHM-082).
  const offeringRows = useMemo(
    () => (showDeleted ? scopedDeletedOfferings : scopedOfferings).filter((w) => Number(w.offering) > 0),
    [showDeleted, scopedOfferings, scopedDeletedOfferings]
  );
  const offeringRange = useDateRangeFilter(offeringRows);
  const offeringPage = usePagination(offeringRange.filtered);
  const totalOfferings = useMemo(() => sumOfferings(offeringRange.filtered), [offeringRange.filtered]);

  // --- Years ---
  const handleAddYear = async (e) => {
    e.preventDefault();
    if (yearForm.startDate && yearForm.endDate && yearForm.endDate < yearForm.startDate) {
      notify('End date must be after the start date.', 'error'); return;
    }
    const id = editKind('year');
    const saved = id ? await years.update(id, yearForm) : await years.add(yearForm);
    if (saved) {
      setYearForm(EMPTY_YEAR); setEditing(null);
      notify(id ? 'Academic year updated.' : 'Academic year created and set as current.', 'success');
    }
  };
  const startEditYear = (y) => {
    setYearForm({ name: y.name || '', startDate: y.startDate || '', endDate: y.endDate || '' });
    setEditing({ kind: 'year', rawId: y.rawId });
  };
  const makeCurrentYear = async (y) => {
    if (await years.setActive(y.id)) {
      scopeTouched.current = false; // let the scope follow the new current year
      notify(`${y.name} is now the current year.`, 'success');
    }
  };

  // --- Grades & sections ---
  const createGrades = (drafts: Array<{ grade: Partial<SundayGrade>; sectionCount: number }>) =>
    grades.addBatch(drafts.map((d) => ({ grade: d.grade, letters: nextSectionLetters([], d.sectionCount) })));

  /** Writes one student row per child, ids numbered in sequence. */
  const enrollStudents = async (rows: Enrolment[]) => {
    const first = parseInt(nextRecordId('STU', rawStudents.map((r) => ({ recordId: r.studentId }))).split('-')[1], 10);
    try {
      for (let i = 0; i < rows.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await studentsApi.create({ ...rows[i], studentId: `STU-${first + i}`, status: 'ACTIVE' });
      }
      await refetch();
      return true;
    } catch (err) {
      await refetch();
      notify(err.message || 'Could not enrol the students.', 'error');
      return false;
    }
  };

  const moveStudent = async (student: EnrolledStudent, classId: string) => {
    const existing = rawStudents.find((r) => r.studentId === student.id);
    if (!existing) { notify('That student no longer exists.', 'error'); return false; }
    try {
      await studentsApi.update(existing.id, { ...existing, classId });
      await refetch();
      return true;
    } catch (err) {
      notify(err.message || 'Could not move the student.', 'error');
      return false;
    }
  };

  // --- Teachers ---
  const handleAddTeacher = async (e) => {
    e.preventDefault();
    if (!teacherForm.classId) { notify('Please assign the teacher to a section.', 'error'); return; }
    const id = editKind('teacher');

    const name = teacherForm.name.trim();
    const phone = teacherForm.phone.trim();
    // Addresses are matched and stored lower case, so "A@B.com" and "a@b.com"
    // cannot both be registered (CHM-135).
    const email = teacherForm.email.trim().toLowerCase();

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notify('Enter a valid email address.', 'error'); return;
    }

    // Checked against every teacher, not just the ones in scope, so a clash
    // with another section's teacher is still caught.
    const others = teachers.items.filter((x) => x.rawId !== id);
    const sameName = (x) => String(x.name || '').trim().toLowerCase() === name.toLowerCase();

    // One teacher per section (CHM-138) — but only checked when the section is
    // actually being set or moved. Checking it on every save stopped a row that
    // already sits in its section from being edited at all, including any
    // duplicate created before this rule existed (CHM-137).
    const current = id ? teachers.items.find((x) => x.rawId === id) : null;
    if (!current || current.classId !== teacherForm.classId) {
      const taken = others.find((x) => x.classId === teacherForm.classId);
      if (taken) {
        notify(`${classNameOf(teacherForm.classId)} is already taught by ${taken.name}. Remove them first, or pick another section.`, 'error');
        return;
      }
    }

    // A phone or email may repeat only for the same person teaching a second
    // section — which this form invites. Two different names sharing one
    // number or address is the mistake being blocked (CHM-134).
    const phoneClash = phone && others.find((x) => String(x.phone || '').trim() === phone && !sameName(x));
    if (phoneClash) {
      notify(`That phone number is already used by ${phoneClash.name}.`, 'error'); return;
    }
    const emailClash = email && others.find((x) => String(x.email || '').trim().toLowerCase() === email && !sameName(x));
    if (emailClash) {
      notify(`That email address is already used by ${emailClash.name}.`, 'error'); return;
    }

    const payload = { ...teacherForm, name, phone, email };
    const saved = id ? await teachers.update(id, payload) : await teachers.add(payload);
    if (saved) {
      setTeacherForm(EMPTY_TEACHER); setEditing(null);
      notify(id ? 'Teacher updated.' : 'Teacher added.', 'success');
    }
  };
  const teacherFormRef = useRef<HTMLDivElement>(null);
  const startEditTeacher = (x) => {
    setTeacherForm({ name: x.name || '', phone: x.phone || '', email: x.email || '', classId: x.classId || '', username: x.username || '' });
    setEditing({ kind: 'teacher', rawId: x.rawId });
    // A half-finished "Create login" panel sits directly under this form. Left
    // open, the two stacked forms meant Enter could submit the login instead of
    // the edit (CHM-137).
    setLoginFor(null);
    // The form sits above the list, so without this the page never moves and
    // pressing Edit looks like it did nothing at all (CHM-137).
    requestAnimationFrame(() =>
      teacherFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const openCreateLogin = (x) => {
    setLoginFor({ rawId: x.rawId, name: x.name || '' });
    setLoginForm({ username: suggestUsername(x.name), password: '' });
  };

  /**
   * Makes the system user for a teacher (role "Sunday School Teacher", access
   * to Sunday School only) and stamps the username on every one of their
   * rows — a teacher with two sections has two rows, and the server allows
   * whatever sections carry their username.
   */
  const submitCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginFor) return;
    const username = loginForm.username.trim().toLowerCase();
    const password = loginForm.password;
    if (!username) { notify('Username is required.', 'error'); return; }
    if (password.length < 6) { notify('Password must be at least 6 characters.', 'error'); return; }
    setSavingLogin(true);
    try {
      const existingUsers = getList<any>(await usersApi.list());
      const taken = existingUsers.find((u) => String(u.username || '').toLowerCase() === username);
      if (taken && taken.userRole !== TEACHER_ROLE) {
        throw new Error(`"${username}" is already used by a ${taken.userRole || 'user'}. Pick another username.`);
      }
      if (!taken) {
        await usersApi.create({
          name: loginFor.name,
          username,
          password,
          userRole: TEACHER_ROLE,
          permissions: JSON.stringify({ 'Sunday School': 'Full Access' }),
          recordId: nextRecordId('USR', existingUsers),
          status: 'ACTIVE'
        } as any);
      }
      const sameName = teachers.items.filter((x) =>
        x.rawId === loginFor.rawId
        || (String(x.name || '').trim().toLowerCase() === loginFor.name.trim().toLowerCase() && !x.username));
      for (const row of sameName) {
        // eslint-disable-next-line no-await-in-loop
        await teachers.update(row.rawId as number, { username });
      }
      notify(taken
        ? `Linked ${loginFor.name} to the existing login "${username}".`
        : `Login "${username}" created for ${loginFor.name}. They can sign in now.`, 'success');
      setLoginFor(null);
      setLoginForm(EMPTY_LOGIN);
    } catch (err) {
      notify(err.message || 'Could not create the login.', 'error');
    } finally {
      setSavingLogin(false);
    }
  };

  // --- Students (visitor children; members come in through Classes) ---
  // Students live in their own table with their own key (`id`, numeric) and
  // human id (`studentId`). Edits send the whole row back, as everywhere else.
  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!studentForm.classId) { notify('Please pick the section.', 'error'); return; }
    const editingRawId = editKind('student');

    setSavingStudent(true);
    try {
      if (editingRawId) {
        const existing = rawStudents.find((s) => s.id === editingRawId);
        if (!existing) throw new Error('That student no longer exists. Refresh and try again.');
        await studentsApi.update(editingRawId, { ...existing, ...studentForm, id: editingRawId });
      } else {
        await studentsApi.create({
          ...studentForm,
          studentId: nextRecordId('STU', rawStudents.map((s) => ({ recordId: s.studentId }))),
          status: 'ACTIVE'
        });
      }
      await refetch();
      setStudentForm(EMPTY_STUDENT);
      setStudentFormOpen(false);
      setEditing(null);
      notify(editingRawId ? 'Student updated.' : 'Student added.', 'success');
    } catch (err) {
      notify(err.message || 'Could not save the student.', 'error');
    } finally {
      setSavingStudent(false);
    }
  };
  const startEditStudent = (s) => {
    setStudentForm({
      name: s.name || '', dob: s.dob || '', parentName: s.parentName || '',
      phone: s.phone || '', classId: s.classId || ''
    });
    setEditing({ kind: 'student', rawId: s.id ? rawStudents.find((r) => r.studentId === s.id)?.id : undefined });
    setStudentFormOpen(true);
  };
  /** From a section card: a non-member child, with the section already chosen. */
  const addVisitorTo = (sectionId: string) => {
    setStudentForm({ ...EMPTY_STUDENT, classId: sectionId });
    setEditing(null);
    setStudentFormOpen(true);
  };

  // --- Sunday: attendance per section ---------------------------------------
  const weeklyStudents = weeklyForm.classId
    ? students.filter((s) => s.classId === weeklyForm.classId)
    : [];
  const marking = Boolean(weeklyForm.classId);
  const editingRegisterId = editKind('register');

  /** What the chosen Sunday looks like, section by section. */
  const sundayRows = useMemo(() => sortedSections.map((c) => {
    const sheet = scopedWeekly.find((w) => w.classId === c.id && w.date === sundayDate && w.attendanceRawId);
    const moneyOnly = sheet ? undefined : scopedWeekly.find((w) => w.classId === c.id && w.date === sundayDate);
    const ticks = Object.values(sheet?.attendance || {});
    return {
      id: c.id,
      label: classNameOf(c.id),
      students: students.filter((s) => s.classId === c.id).length,
      teachers: teachers.items.filter((x) => x.classId === c.id).map((x) => x.name).join(', '),
      sheet,
      present: ticks.filter(Boolean).length,
      absent: ticks.filter((v) => !v).length,
      offering: sheet ? sheet.offering : moneyOnly?.offering
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sortedSections, scopedWeekly, sundayDate, students, teachers.items, grades.items]);
  const markedCount = sundayRows.filter((r) => r.sheet).length;
  const sundayOffering = sumOfferings(sundayRows.map((r) => ({ offering: r.offering })));

  /** The sheet already saved for this section on this date, if there is one. */
  const duplicateRegister = useMemo(
    () => (weeklyForm.date && weeklyForm.classId)
      ? weekly.items.find((w) => w.attendanceRawId && w.date === weeklyForm.date
          && w.classId === weeklyForm.classId && w.attendanceRawId !== editingRegisterId)
      : undefined,
    [weekly.items, weeklyForm.date, weeklyForm.classId, editingRegisterId]
  );

  const loadRegister = (w: WeeklyRegister) => {
    setWeeklyForm({ date: w.date || '', classId: w.classId || '', studentId: '', offering: w.offeringRawId ? String(w.offering ?? '') : '' });
    // Everyone on the section roll today, with the saved ticks laid over them:
    // a child who joined since the sheet was saved starts present.
    const next: Record<string, boolean> = {};
    students.filter((s) => s.classId === w.classId).forEach((s) => { next[s.id] = true; });
    Object.entries(w.attendance || {}).forEach(([id, v]) => { next[id] = Boolean(v); });
    setAttendanceMap(next);
    setEditing({ kind: 'register', rawId: w.attendanceRawId as number });
    if (w.date) setSundayDate(w.date);
    setActiveTab('sunday');
    setSundaySubTab('mark');
  };

  /** Mark a section for the chosen Sunday — or reopen it if already marked. */
  const openMark = (row: typeof sundayRows[number]) => {
    if (row.sheet) { loadRegister(row.sheet); return; }
    // Everyone starts marked present; the teacher unticks absentees.
    const next = {};
    students.filter((s) => s.classId === row.id).forEach((s) => { next[s.id] = true; });
    setAttendanceMap(next);
    setEditing(null);
    setWeeklyForm({ ...EMPTY_WEEKLY, date: sundayDate, classId: row.id, offering: row.offering ? String(row.offering) : '' });
  };

  const markAll = (present: boolean) => {
    const next = {};
    weeklyStudents.forEach((s) => { next[s.id] = present; });
    setAttendanceMap(next);
  };

  /** Copies the ticks from this section's most recent sheet before the chosen date. */
  const copyPreviousWeek = () => {
    const previous = weekly.items.find((w) => w.classId === weeklyForm.classId
      && Object.keys(w.attendance || {}).length > 0
      && (!weeklyForm.date || String(w.date) < weeklyForm.date)
      && w.attendanceRawId !== editingRegisterId);
    if (!previous) { notify('No earlier Sunday for this section to copy.', 'error'); return; }
    const next: Record<string, boolean> = {};
    weeklyStudents.forEach((s) => { next[s.id] = previous.attendance?.[s.id] ?? true; });
    setAttendanceMap(next);
    notify(`Copied attendance from ${formatDate(previous.date)}.`, 'success');
  };

  const closeMark = () => {
    setWeeklyForm(EMPTY_WEEKLY);
    setAttendanceMap({});
    setEditing(null);
  };

  const handleSaveWeekly = async (e) => {
    e.preventDefault();
    if (!weeklyForm.classId || !weeklyForm.date) { notify('Section and date are required.', 'error'); return; }
    if (weeklyStudents.length === 0) { notify('This section has no students to mark.', 'error'); return; }
    // The same section marked twice on one Sunday is two sheets, and the
    // money on both would count. Open the existing one instead.
    if (duplicateRegister) {
      notify('This section is already marked for that date — opening it to edit.', 'error');
      loadRegister(duplicateRegister);
      return;
    }
    const payload = { ...weeklyForm, attendance: attendanceMap };
    const saved = editingRegisterId
      ? await weekly.update(editingRegisterId, payload)
      : await weekly.add(payload);
    if (saved) {
      const label = classNameOf(weeklyForm.classId);
      closeMark();
      notify(`${label} ${editingRegisterId ? 'updated' : 'marked'} for ${formatDate(weeklyForm.date)}.`, 'success');
    }
  };

  // --- Offerings: one child's gift, or editing any row --------------------
  const handleSaveOffering = async (e) => {
    e.preventDefault();
    if (!offeringForm.classId || !offeringForm.date) { notify('Section and date are required.', 'error'); return; }
    if (!(Number(offeringForm.offering) > 0)) { notify('Enter the amount.', 'error'); return; }
    const id = editKind('offering');
    if (!id && !offeringForm.studentId) { notify('Pick the student. Section totals are entered on the Mark Attendance screen.', 'error'); return; }
    const saved = id
      ? await weekly.updateOffering(id, offeringForm)
      : await weekly.addOffering(offeringForm);
    if (saved) {
      setOfferingForm(EMPTY_WEEKLY);
      setOfferingFormOpen(false);
      setEditing(null);
      notify(id ? 'Offering updated.' : 'Offering saved.', 'success');
    }
  };
  const startEditOffering = (r: SundayOfferingRow) => {
    setOfferingForm({ date: r.date || '', classId: r.classId || '', studentId: r.studentId || '', offering: String(r.offering ?? '') });
    setEditing({ kind: 'offering', rawId: r.rawId as number });
    setOfferingFormOpen(true);
    setSundaySubTab('history');
    setHistoryView('offerings');
  };
  const openGiftForm = () => {
    setOfferingForm({ ...EMPTY_WEEKLY, date: sundayDate });
    setEditing(null);
    setOfferingFormOpen(true);
  };

  // --- Delete ---
  const confirmDelete = async (reason: string) => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    let ok = false;
    if (target.kind === 'register') ok = await weekly.voidRegister(target.rawId, reason);
    else if (target.kind === 'offering') ok = await weekly.voidOffering(target.rawId, reason);
    else if (target.kind === 'grade') {
      // Its sections go with it; they are empty (the button is disabled otherwise).
      const gradeId = grades.items.find((g) => g.rawId === target.rawId)?.id;
      const owned = classes.items.filter((c) => c.gradeId === gradeId);
      ok = await grades.void(target.rawId, reason);
      for (const c of owned) {
        // eslint-disable-next-line no-await-in-loop
        if (ok) ok = await classes.void(c.rawId as number, reason);
      }
    }
    else if (target.kind === 'section') ok = await classes.void(target.rawId, reason);
    else if (target.kind === 'exam') ok = await exams.void(target.rawId, reason);
    else {
      try {
        const existing = rawStudents.find((s) => s.id === target.rawId);
        if (!existing) throw new Error('That student no longer exists.');
        await studentsApi.update(target.rawId, {
          ...existing, id: target.rawId, status: 'VOID', deleteReason: reason, deletedAt: voidStamp()
        } as any);
        await refetch();
        ok = true;
      } catch (err) {
        notify(err.message || 'Could not remove the student.', 'error');
      }
    }
    if (ok) notify(`${target.label} deleted.`, 'success');
  };

  // --- Exams -----------------------------------------------------------------
  const examScopeLabel = (x: SsRow<Exam>) => x.classId
    ? classNameOf(x.classId)
    : x.gradeId ? (grades.items.find((g) => g.id === x.gradeId)?.name || 'One grade') : 'All grades';

  /** Sections in scope that sit the exam, with their saved marks row. */
  const examProgress = (x: SsRow<Exam>) => {
    const secs = examSections(x, scopedClasses);
    const done = secs.filter((c) => marksEntered(marksFor(marks.items, x, c.id as string)) > 0).length;
    // Pass rate over everyone who sat, across every section that has marks.
    let passed = 0; let sat = 0;
    secs.forEach((c) => {
      const row = marksFor(marks.items, x, c.id as string);
      if (!row) return;
      const tally = tallyResults(students.filter((st) => st.classId === c.id).map((st) => st.id), row.scores, x);
      passed += tally.passed; sat += tally.sat;
    });
    return { sections: secs.length, done, passed, sat, passPct: sat > 0 ? Math.round((passed / sat) * 1000) / 10 : null };
  };

  const openCreateExams = () => {
    setExamYear(yearScope || activeYear?.id || '');
    setExamRows([{ ...EMPTY_EXAM_ROW }]);
    setEditing(null);
    setExamFormOpen(true);
  };
  const setExamRow = (i: number, patch: Partial<ExamRow>) =>
    setExamRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submitExamRows = async (e) => {
    e.preventDefault();
    if (!examYear) { notify('Pick the academic year.', 'error'); return; }
    const filled = examRows.filter((r) => r.name.trim() || r.date);
    if (filled.length === 0) { notify('Add at least one exam.', 'error'); return; }
    const items: Partial<Exam>[] = [];
    for (const r of filled) {
      const name = r.name.trim();
      if (!name) { notify(`Row ${examRows.indexOf(r) + 1}: exam name is required.`, 'error'); return; }
      if (!r.date) { notify(`${name}: date is required.`, 'error'); return; }
      if (!(Number(r.totalMarks) > 0)) { notify(`${name}: total marks must be more than 0.`, 'error'); return; }
      const pass = Number(r.passMarks);
      if (!(pass >= 0) || pass > Number(r.totalMarks)) { notify(`${name}: pass marks must be between 0 and the total.`, 'error'); return; }
      const candidate = { name, subject: r.subject.trim(), date: r.date, academicYearId: examYear, gradeId: r.gradeId || '' };
      if (exams.items.some((x) => sameExam(x, candidate))) {
        notify(`${examTitle(candidate)} already exists on ${formatDate(r.date)} for that year and grade.`, 'error'); return;
      }
      if (items.some((x) => sameExam(x, candidate))) {
        notify(`${examTitle(candidate)} is entered twice on ${formatDate(r.date)} in this form.`, 'error'); return;
      }
      items.push({ ...candidate, totalMarks: Number(r.totalMarks), passMarks: pass, classId: '' });
    }
    setSavingExams(true);
    const ok = await exams.addBatch(items);
    setSavingExams(false);
    if (ok) {
      notify(`${items.length} exam${items.length === 1 ? '' : 's'} created.`, 'success');
      setExamFormOpen(false);
    }
  };

  const startEditExam = (x: SsRow<Exam>) => {
    setExamEdit({
      name: x.name || '', subject: x.subject || '', date: x.date || '', totalMarks: String(x.totalMarks ?? ''),
      passMarks: String(passMarkFor(x)),
      gradeId: x.gradeId || '', academicYearId: x.academicYearId || '', classId: x.classId || ''
    });
    setEditing({ kind: 'exam', rawId: x.rawId as number });
    setExamFormOpen(true);
  };

  // "Open the record" from the Approvals inbox. These records have no view of
  // their own: a live student, year, teacher, exam or offering opens in its
  // form; the rest (and anything deleted) open on the tab that lists them.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, {
      student: studentsApi.get, year: academicYears.get, grade: sundayGrades.get, section: sundayClasses.get,
      teacher: sundayTeachers.get, attendance: sundayAttendance.get, offering: sundayOfferings.get,
      exam: sundayExams.get, marks: sundayExamMarks.get
    });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const r = found.record;
    const live = r.status !== 'VOID';
    const asRow = { ...r, id: r.recordId, rawId: r.id };
    setEditing(null);
    switch (found.kind) {
      case 'student':
      case 'year':
      case 'grade':
      case 'section':
        setActiveTab('classes'); setClassesSubTab('grades');
        if (live && found.kind === 'student') {
          startEditStudent({ ...r, id: r.studentId });
          setEditing({ kind: 'student', rawId: r.id });
        }
        if (live && found.kind === 'year') { setYearsOpen(true); startEditYear(asRow); }
        break;
      case 'teacher':
        setActiveTab('classes'); setClassesSubTab('teachers');
        if (live) startEditTeacher(asRow);
        break;
      case 'attendance':
      case 'offering':
        setActiveTab('sunday'); setSundaySubTab('history');
        setHistoryView(found.kind === 'offering' ? 'offerings' : 'attendance');
        if (live && found.kind === 'offering') startEditOffering(asRow);
        break;
      default:
        setActiveTab('exams');
        setExamSubTab(found.kind === 'marks' || teacherLogin ? 'marks' : 'create');
        if (live && found.kind === 'exam' && !teacherLogin) startEditExam(asRow);
    }
  });
  const submitExamEdit = async (e) => {
    e.preventDefault();
    const id = editKind('exam');
    if (!id) return;
    if (!examEdit.name.trim()) { notify('Exam name is required.', 'error'); return; }
    if (!(Number(examEdit.totalMarks) > 0)) { notify('Total marks must be more than 0.', 'error'); return; }
    const pass = Number(examEdit.passMarks);
    if (!(pass >= 0) || pass > Number(examEdit.totalMarks)) { notify('Pass marks must be between 0 and the total.', 'error'); return; }
    const candidate = {
      name: examEdit.name.trim(), subject: examEdit.subject.trim(), date: examEdit.date,
      academicYearId: examEdit.academicYearId, gradeId: examEdit.gradeId || ''
    };
    if (exams.items.some((x) => x.rawId !== id && sameExam(x, candidate))) {
      notify(`${examTitle(candidate)} already exists on ${formatDate(candidate.date)} for that year and grade.`, 'error'); return;
    }
    const ok = await exams.update(id, {
      name: examEdit.name.trim(), subject: examEdit.subject.trim(), date: examEdit.date,
      totalMarks: Number(examEdit.totalMarks), passMarks: pass,
      // A legacy per-section exam keeps its section; a school-wide one keeps its year.
      ...(examEdit.classId ? {} : { gradeId: examEdit.gradeId || '' })
    });
    if (ok) { notify('Exam updated.', 'success'); setExamFormOpen(false); setEditing(null); }
  };

  // --- Marks: exam -> section -> students --------------------------------------
  const selectedExam = exams.items.find((x) => x.id === selectedExamId);
  const examSectionRows = useMemo(() => {
    if (!selectedExam) return [];
    return examSections(selectedExam, sortedSections).map((c) => {
      const row = marksFor(marks.items, selectedExam, c.id as string);
      const roll = students.filter((s) => s.classId === c.id);
      const tally = row ? tallyResults(roll.map((s) => s.id), row.scores, selectedExam) : null;
      return { id: c.id as string, label: classNameOf(c.id), students: roll.length, entered: marksEntered(row), hasRow: Boolean(row), tally };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam, sortedSections, marks.items, students, grades.items]);

  const marksStudents = marksSectionId ? students.filter((s) => s.classId === marksSectionId) : [];

  const openMarks = (sectionId: string) => {
    if (!selectedExam) return;
    const row = marksFor(marks.items, selectedExam, sectionId);
    const next = {};
    students.filter((s) => s.classId === sectionId).forEach((s) => {
      next[s.id] = row?.scores?.[s.id] || { obtained: '', remarks: '' };
    });
    setMarksMap(next);
    setMarksSectionId(sectionId);
  };
  const closeMarks = () => { setMarksSectionId(''); setMarksMap({}); };

  const handleSaveMarks = async () => {
    if (!selectedExamId || !selectedExam || !marksSectionId) return;
    // A blank sheet must not report success (CHM-083): require at least one
    // score actually entered before saving.
    const anyEntered = Object.values(marksMap || {})
      .some((m) => String((m as { obtained?: unknown })?.obtained ?? '').trim() !== '');
    if (!anyEntered) {
      notify('Enter at least one mark before saving.', 'error');
      return;
    }
    // 150 out of 100 is a typo, not a score; name the student so it is easy to find.
    for (const s of marksStudents) {
      const problem = markProblem(marksMap[s.id]?.obtained, selectedExam.totalMarks);
      if (problem) { notify(`${s.name}: mark ${problem}.`, 'error'); return; }
    }
    if (await marks.save(selectedExamId, marksSectionId, marksMap)) {
      notify(`Marks saved for ${classNameOf(marksSectionId)}.`, 'success');
      closeMarks();
    }
  };

  // --- Report cards: section -> students -----------------------------------------
  /** Every exam this student sat, with their score. */
  const reportRowsFor = (studentId: string) => {
    const student = students.find((s) => s.id === studentId);
    const section = classes.items.find((c) => c.id === student?.classId);
    if (!student || !section) return [];
    return exams.items
      .filter((exam) => examAppliesTo(exam, section))
      .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))
      .map((exam) => {
        const score = marksFor(marks.items, exam, section.id as string)?.scores?.[studentId];
        if (!score || String(score.obtained ?? '').trim() === '') return null;
        const obtained = parseFloat(String(score.obtained)) || 0;
        const total = parseFloat(String(exam.totalMarks)) || 0;
        return {
          id: exam.id,
          exam: exam.name,
          subject: exam.subject || '',
          date: exam.date,
          className: classNameOf(section.id),
          obtained,
          total,
          passMarks: passMarkFor(exam),
          pct: total > 0 ? (obtained / total) * 100 : 0,
          result: resultOf(score.obtained, exam),
          remarks: score.remarks
        };
      })
      .filter(Boolean);
  };
  const reportCardData = (student: { id: string; name?: string; classId?: string }) => {
    const rows = reportRowsFor(student.id);
    return {
      studentName: student.name,
      className: classNameOf(student.classId),
      academicYear: yearNameOf(classes.items.find((c) => c.id === student.classId)?.academicYearId),
      rowCount: rows.length,
      rows: rows.map((row, i) => ({
        index: i + 1,
        exam: row.exam,
        subject: row.subject || '-',
        date: formatDate(row.date),
        total: row.total,
        scored: row.obtained,
        result: row.result
      }))
    };
  };
  const reportStudents = useMemo(
    () => (reportSectionId ? students.filter((s) => s.classId === reportSectionId) : [])
      .map((s) => {
        const rows = reportRowsFor(s.id);
        const obtained = rows.reduce((n, r) => n + r.obtained, 0);
        const total = rows.reduce((n, r) => n + r.total, 0);
        const passed = rows.filter((r) => r.result === 'PASS').length;
        return { ...s, exams: rows.length, passed, failed: rows.length - passed, pct: total > 0 ? (obtained / total) * 100 : null };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportSectionId, students, exams.items, marks.items, classes.items]
  );

  // --- Summary ---------------------------------------------------------------
  // The figures are worked out in ./analytics; here the scoped rows are only
  // labelled the way the rest of the screen names them.
  const summarySections = useMemo(
    () => sortedSections.map((c) => ({ ...c, id: c.id as string, label: classNameOf(c.id), gradeName: gradeOf(c)?.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedSections, grades.items]
  );
  const summaryTerm = useMemo(
    () => years.items.find((y) => y.id === (yearScope || activeYear?.id)) || null,
    [years.items, yearScope, activeYear?.id]
  );
  // A teacher sees only their own sections, so the unenrolled list is not theirs.
  const unassignedStudents = useMemo(
    () => (teacherLogin ? undefined : studentsWithoutSection(students, classes.items.map((c) => c.id))),
    [teacherLogin, students, classes.items]
  );

  // --- Setup checklist -------------------------------------------------------
  const setup = [
    { id: 'year', tab: 'grades', years: true, label: 'Academic year', done: years.items.length > 0 },
    { id: 'grades', tab: 'grades', label: 'Grades & sections', done: scopedClasses.length > 0 },
    { id: 'teachers', tab: 'teachers', label: 'Teachers', done: scopedTeachers.length > 0 },
    { id: 'students', tab: 'grades', label: 'Students enrolled', done: scopedStudents.length > 0 }
  ];
  const setupIncomplete = setup.some((s) => !s.done);

  // --- Styles ---
  const sub = { fontSize: '12px', color: t.textFaint };

  const panel = {
    backgroundColor: t.bgSubtle,
    border: `1px solid ${t.border}`,
    borderRadius: t.radius.lg,
    padding: t.spacing.lg,
    marginBottom: t.spacing.lg
  };

  const checklistRow = {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px', borderBottom: `1px solid ${t.borderDashed}`,
    color: t.textMain, fontSize: t.fontSize.md
  };

  const setupWarning = {
    padding: t.spacing.md,
    marginBottom: t.spacing.md,
    borderRadius: t.radius.lg,
    backgroundColor: t.bgSubtle,
    border: `1px solid ${t.warning}`,
    color: t.warning,
    fontWeight: t.fontWeight.bold,
    fontSize: t.fontSize.md
  };
  const editingBanner = { ...setupWarning, border: `1px solid ${t.info}`, color: t.info };

  const deletedColumns = [
    { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> }
  ];

  const showDeletedToggle = (
    <Button size="sm" variant={showDeleted ? 'danger' : 'ghost'} onClick={() => { setShowDeleted((v) => !v); weeklyPage.reset(); offeringPage.reset(); }}>
      {showDeleted ? '🗑️ Showing deleted' : 'Show deleted'}
    </Button>
  );

  const noClassesWarning = teacherLogin ? (
    <div style={setupWarning}>
      ⚠️ Your login is not linked to a section yet. Ask the administrator to add you under Classes › Teachers.
    </div>
  ) : (
    <div style={setupWarning}>
      ⚠️ No sections in {scopeName}. Create grades under <strong>Classes</strong> first{yearScope ? ', or pick another year above' : ''}.
      <Button size="sm" variant="ghost" style={{ marginLeft: '12px' }} onClick={() => { setActiveTab('classes'); setClassesSubTab('grades'); }}>Go to Classes</Button>
    </div>
  );



  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PromptDialog
        open={Boolean(deleting)}
        title={`Delete ${deleting?.label || ''}`}
        message="It will move to the deleted list, with this reason:"
        placeholder="e.g. Entered twice, wrong section"
        confirmLabel="Delete"
        destructive
        isDarkMode={isDarkMode}
        onSubmit={confirmDelete}
        onCancel={() => setDeleting(null)}
      />

      <PageHeader
        icon="🏫"
        title="Sunday School"
        activeTab={activeTab}
        // Leaving a tab abandons whatever was half-typed there, so the mark-attendance
        // sheet is cleared rather than reappearing filled in later (CHM-080).
        onTabChange={(k) => { if (k !== activeTab) closeMark(); setActiveTab(k); setShowDeleted(false); }}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'sunday', label: '📅 Sunday' },
          ...(teacherLogin ? [] : [{ key: 'classes', label: '🎓 Classes' }]),
          { key: 'exams', label: '📝 Exams' }
        ]}
        actions={
          <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            {teacherLogin && <Badge tone="accent">Teacher: {sortedSections.map((c) => classNameOf(c.id)).join(', ') || 'no section'}</Badge>}
            {activeYear
              ? <Badge tone="success">Current year: {activeYear.name}</Badge>
              : <Badge tone="error">No current year{teacherLogin ? '' : ' — set one under Classes › Academic Years'}</Badge>}
            {/* Only worth showing once there is something to choose between. */}
            {years.items.length > 1 && (
              <Select
                placeholder=""
                containerStyle={{ margin: 0, minWidth: '170px' }}
                value={yearScope}
                onChange={(e) => chooseScope(e.target.value)}
                options={[
                  ...years.items.map((y) => ({ value: y.id, label: `Viewing: ${y.name}` })),
                  { value: ALL_YEARS, label: 'Viewing: all years' }
                ]}
              />
            )}
          </span>
        }
      />

      {/* ================================================== 📅 SUNDAY */}
      {activeTab === 'sunday' && (
        <>
          {noClasses && noClassesWarning}
          <SubTabs
            value={sundaySubTab}
            onChange={(k) => { setSundaySubTab(k); setShowDeleted(false); weeklyPage.reset(); offeringPage.reset(); }}
            tabs={[
              { key: 'mark', label: marking ? '✎ Marking…' : 'Mark Attendance' },
              { key: 'history', label: 'History' }
            ]}
          />

          {sundaySubTab === 'mark' && !marking && (
            <>
              <div style={{ ...panel, display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Input label="Sunday" type="date" containerStyle={{ margin: 0 }}
                  hint={sundayDate && !isSunday(sundayDate) ? 'That date is not a Sunday.' : undefined}
                  value={sundayDate} onChange={(e) => setSundayDate(e.target.value)} />
                {/* Step a week at a time without opening the date picker (CHM-133). */}
                <Button variant="ghost" size="sm" title="Previous Sunday" aria-label="Previous Sunday"
                  onClick={() => setSundayDate(shiftSunday(sundayDate, -1))}>←</Button>
                <Button variant="ghost" size="sm" title="Next Sunday" aria-label="Next Sunday"
                  onClick={() => setSundayDate(shiftSunday(sundayDate, 1))}>→</Button>
                <Button variant="ghost" size="sm" onClick={() => setSundayDate(lastSunday())}>Last Sunday</Button>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div style={{ fontSize: '22px', fontWeight: 900, color: markedCount === sundayRows.length && sundayRows.length ? t.income : t.textMain }}>
                    {markedCount} / {sundayRows.length} <span style={{ ...sub, fontSize: '13px' }}>sections marked</span>
                  </div>
                  <div style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(sundayOffering)} <span style={sub}>offering so far</span></div>
                </div>
              </div>

              <TablePanel
                title={<>Sections — {formatDate(sundayDate)}</>}
                   isDarkMode={isDarkMode}
                minWidth="720px"
                columns={[
                  { key: 'label', header: 'Section', render: (r) => <><strong>{r.label}</strong><br /><span style={sub}>{r.teachers || 'No teacher'}</span></> },
                  { key: 'students', header: 'Students', align: 'center' },
                  {
                    key: 'status', header: 'Attendance',
                    render: (r) => r.sheet
                      ? <><Badge tone="success">✔ Marked</Badge> <span style={{ color: t.textMain }}>{r.present} present</span>{r.absent ? <span style={{ color: t.danger }}>, {r.absent} absent</span> : null}</>
                      : <Badge tone="neutral">Not marked</Badge>
                  },
                  {
                    key: 'offering', header: 'Offering', align: 'right', nowrap: true,
                    render: (r) => r.offering ? <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.offering)}</span> : <span style={sub}>—</span>
                  },
                ]}
                rows={sundayRows}
                rowKey="id"
                emptyMessage={`No sections in ${scopeName}.`}
              />
              <div style={{ ...sub, marginTop: '8px' }}>
                Mark each section and note the section's offering in the same panel. A gift from one child goes under <strong>History › Offerings</strong>.
              </div>
            </>
          )}

          {sundaySubTab === 'mark' && marking && (
            <form onSubmit={handleSaveWeekly}>
              <div style={editingBanner}>
                {editingRegisterId ? '✎ Editing' : 'Marking'} <strong>{classNameOf(weeklyForm.classId)}</strong> for <strong>{formatDate(weeklyForm.date)}</strong>
                <Button type="button" size="sm" variant="ghost" style={{ marginLeft: '12px' }} onClick={closeMark}>← Back to sections</Button>
              </div>
              {duplicateRegister && (
                <div style={setupWarning}>
                  ⚠️ {classNameOf(weeklyForm.classId)} is already marked for {formatDate(weeklyForm.date)}. Saving opens that one instead.
                  <Button type="button" size="sm" variant="ghost" style={{ marginLeft: '12px' }} onClick={() => loadRegister(duplicateRegister)}>Open it</Button>
                </div>
              )}
              <div style={panel}>
                <FormLayout.Grid columns={2}>
                  <Input label="Sunday" type="date" required
                    value={weeklyForm.date} onChange={(e) => setWeeklyForm({ ...weeklyForm, date: e.target.value })} />
                  <Input label="Section offering (₹)" type="number" min="0" step="0.01" placeholder="0.00"
                    hint="What this section collected this Sunday. Leave blank if none."
                    value={weeklyForm.offering} onChange={(e) => setWeeklyForm({ ...weeklyForm, offering: e.target.value })}
                    style={{ color: t.income, fontWeight: 'bold' }} />
                </FormLayout.Grid>
              </div>

              <div style={panel}>
                <div style={{ ...styles.sectionTitle, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{weeklyStudents.length} students — tick who is present</span>
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}>
                    <Button type="button" size="sm" variant="ghost" onClick={() => markAll(true)}>All present</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => markAll(false)}>All absent</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={copyPreviousWeek}>Copy last Sunday</Button>
                  </span>
                </div>
                {weeklyStudents.length === 0 ? (
                  <div style={{ color: t.textFaint, fontStyle: 'italic', padding: '10px' }}>
                    No students in this section yet.
                  </div>
                ) : (
                  weeklyStudents.map((s) => (
                    <label key={s.id} style={checklistRow}>
                      <input
                        type="checkbox"
                        checked={Boolean(attendanceMap[s.id])}
                        onChange={(e) => setAttendanceMap((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                      />
                      <strong>{s.name}</strong>
                      <span style={sub}>{s.studentId}</span>
                      {/* Parent name helps the teacher identify siblings and
                          who to contact about an absence. */}
                      <span style={{ ...sub, marginLeft: 'auto' }}>
                        {s.parentName ? `Parent: ${s.parentName}` : 'Parent: -'}
                      </span>
                    </label>
                  ))
                )}
                <div style={{ ...sub, padding: '8px 10px 0' }}>
                  {Object.values(attendanceMap).filter(Boolean).length} present · {weeklyStudents.length - Object.values(attendanceMap).filter(Boolean).length} absent
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                <Button type="button" variant="secondary" onClick={closeMark}>Cancel</Button>
                <Button type="submit">{editingRegisterId ? 'Update Attendance' : 'Save Attendance'}</Button>
              </div>
            </form>
          )}

          {sundaySubTab === 'history' && (
            <>
              <div style={{ display: 'flex', gap: '4px', marginBottom: t.spacing.md }}>
                {([['attendance', '📋 Attendance'], ['offerings', '💰 Offerings']] as const).map(([k, label]) => (
                  <Button key={k} size="sm" variant={historyView === k ? 'primary' : 'ghost'} onClick={() => { setHistoryView(k); setShowDeleted(false); }}>{label}</Button>
                ))}
              </div>
              {historyView === 'attendance' && (
              <TablePanel
                title={showDeleted ? 'Deleted attendance' : 'Attendance'}
                actions={<>{showDeletedToggle}<DateRangeFilter {...weeklyRange} onChange={weeklyPage.reset} /></>}
                below={<Pagination {...weeklyPage} itemsOnPage={weeklyPage.currentItems.length} label="Sundays" />}
                   isDarkMode={isDarkMode}
                columns={[
                  { key: 'date', header: 'Sunday', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
                  { key: 'classId', header: 'Section', render: (r) => classNameOf(r.classId) },
                  {
                    key: 'present', header: 'Present', align: 'center',
                    render: (r) => Object.values(r.attendance || {}).filter(Boolean).length
                  },
                  {
                    key: 'absent', header: 'Absent', align: 'center',
                    render: (r) => Object.values(r.attendance || {}).filter((v) => !v).length
                  },
                  ...(showDeleted ? deletedColumns : [
                    {
                      key: 'offering', header: 'Offering', align: 'right' as const, nowrap: true,
                      // Every offering row for that section and Sunday, summed.
                      render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.offering)}</span>
                    },
                    { key: 'createdBy', header: 'Marked By', render: (r) => r.createdBy || '-' },
                    {
                      key: 'actions', header: '', align: 'right' as const,
                      render: (r) => r.attendanceRawId ? (
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                          <Button write variant="ghost" size="sm" onClick={() => loadRegister(r)}>Edit</Button>
                          <Button variant="danger" size="sm" style={styles.deleteBtn}
                            onClick={() => setDeleting({ kind: 'register', rawId: r.attendanceRawId, label: `attendance for ${classNameOf(r.classId)} on ${formatDate(r.date)}` })}>
                            Delete
                          </Button>
                        </div>
                      ) : <span style={sub}>Offering only</span>
                    }
                  ])
                ]}
                rows={weeklyPage.currentItems}
                emptyMessage={showDeleted ? 'Nothing deleted.' : 'No Sundays marked yet.'}
              />
              )}

              {historyView === 'offerings' && (
              <TablePanel
                title={showDeleted ? 'Deleted offerings' : 'Offerings'}
                below={<Pagination {...offeringPage} itemsOnPage={offeringPage.currentItems.length} label="offerings" />}
                   isDarkMode={isDarkMode}
                above={offeringFormOpen ? (
                <form onSubmit={handleSaveOffering}>
                  <div style={panel}>
                    <div style={styles.sectionTitle}>{editKind('offering') ? 'Edit Offering' : 'Gift from one student'}</div>
                    <FormLayout.Grid columns={4}>
                      <Input label="Sunday" type="date" required
                        value={offeringForm.date}
                        onChange={(e) => setOfferingForm({ ...offeringForm, date: e.target.value })} />
                      <Select label="Section" required options={classOptions}
                        value={offeringForm.classId}
                        // Changing section clears a student from the old one.
                        onChange={(e) => setOfferingForm({ ...offeringForm, classId: e.target.value, studentId: '' })} />
                      <Select label="Student" required={!editKind('offering')}
                        placeholder={editKind('offering') ? '— Whole section —' : 'Pick the student…'}
                        options={students.filter((s) => s.classId === offeringForm.classId).map((s) => ({ value: s.id, label: s.name }))}
                        value={offeringForm.studentId}
                        onChange={(e) => setOfferingForm({ ...offeringForm, studentId: e.target.value })} />
                      <Input label="Amount (₹)" type="number" min="0" step="0.01" required placeholder="0.00"
                        value={offeringForm.offering}
                        onChange={(e) => setOfferingForm({ ...offeringForm, offering: e.target.value })}
                        style={{ color: t.income, fontWeight: 'bold' }} />
                    </FormLayout.Grid>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '8px' }}>
                      <Button type="button" variant="secondary" onClick={() => { setOfferingFormOpen(false); setOfferingForm(EMPTY_WEEKLY); cancelEdit(); }}>Cancel</Button>
                      <Button type="submit">{editKind('offering') ? 'Update Offering' : 'Save Offering'}</Button>
                    </div>
                  </div>
                </form>
              ) : (
                <div style={{ ...panel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ color: t.textMuted, fontWeight: 'bold' }}>
                      {showDeleted ? 'Deleted total' : 'Total offerings'}
                      {offeringRange.isActive && <span style={sub}> (filtered dates)</span>}
                    </span>
                    <div style={{ color: showDeleted ? t.textFaint : t.income, fontSize: t.fontSize.xxl, fontWeight: 900 }}>
                      {formatCurrency(totalOfferings)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <DateRangeFilter {...offeringRange} onChange={offeringPage.reset} />
                    {showDeletedToggle}
                    <Button write onClick={openGiftForm}>+ Gift from one student</Button>
                  </div>
                </div>
              )}
                columns={[
                  { key: 'date', header: 'Sunday', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
                  { key: 'classId', header: 'Section', render: (r) => classNameOf(r.classId) },
                  {
                    key: 'studentId', header: 'From',
                    render: (r) => (r.studentId
                      ? studentNameOf(r.studentId)
                      : <span style={{ color: t.textFaint }}>Section total</span>)
                  },
                  {
                    key: 'offering', header: 'Amount', align: 'right', nowrap: true,
                    render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.offering)}</span>
                  },
                  {
                    key: 'receipt', header: 'Receipt', nowrap: true,
                    // The same money as seen in Offerings & Collections › Donations.
                    render: (r) => (r.donationId ? <span style={sub}>REC-{r.donationId}</span> : <span style={sub}>—</span>)
                  },
                  { key: 'createdBy', header: 'Entered By', render: (r) => r.createdBy || '-' },
                  ...(showDeleted ? deletedColumns : [
                    {
                      key: 'actions', header: '', align: 'right' as const,
                      render: (r) => (
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                          <Button write variant="ghost" size="sm" onClick={() => startEditOffering(r)}>Edit</Button>
                          <Button variant="danger" size="sm" style={styles.deleteBtn}
                            onClick={() => setDeleting({ kind: 'offering', rawId: r.rawId, label: `offering of ${formatCurrency(r.offering)} on ${formatDate(r.date)}` })}>
                            Delete
                          </Button>
                        </div>
                      )
                    }
                  ])
                ]}
                rows={offeringPage.currentItems}
                emptyMessage={showDeleted ? 'Nothing deleted.' : 'No offerings yet. Section totals are entered while marking attendance.'}
              />
              )}
            </>
          )}
        </>
      )}

      {/* ================================================== 🎓 CLASSES */}
      {activeTab === 'classes' && (
        <>
          {setupIncomplete && (
            <div style={{ ...panel, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ color: t.textMain }}>Setup:</strong>
              {setup.map((s) => (
                <Button key={s.id} size="sm" variant={s.done ? 'ghost' : 'secondary'} onClick={() => { setClassesSubTab(s.tab); if (s.years) setYearsOpen(true); }}>
                  {s.done ? '✔' : '○'} {s.label}
                </Button>
              ))}
            </div>
          )}
          <SubTabs
            value={classesSubTab}
            onChange={(k) => { setClassesSubTab(k); cancelEdit(); }}
            tabs={[
              { key: 'grades', label: 'Grades & Sections' },
              { key: 'teachers', label: 'Teachers' }
            ]}
          />

          {classesSubTab === 'grades' && (
            <>
              {years.items.length === 0 && (
                <div style={setupWarning}>
                  ⚠️ Create an academic year first.
                  <Button size="sm" variant="ghost" style={{ marginLeft: '12px' }} onClick={() => setYearsOpen(true)}>Create year</Button>
                </div>
              )}
              {/* Years are set up once and rarely touched, so they live behind a button. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: t.spacing.sm }}>
                <Button size="sm" variant={yearsOpen ? 'secondary' : 'ghost'} onClick={() => setYearsOpen((v) => !v)}>
                  {yearsOpen ? 'Hide academic years' : '📆 Academic years'}
                </Button>
              </div>
              {yearsOpen && (
                <div style={panel}>
                  <FormLayout
                    title={editKind('year') ? 'Edit Academic Year' : 'Create Academic Year'}
                    submitLabel={editKind('year') ? 'Update Academic Year' : 'Save Academic Year'}
                    onSubmit={handleAddYear}
                    onCancel={editKind('year') ? cancelEdit : undefined}
                    maxWidth="100%"
                  >
                    <FormLayout.Grid columns={3}>
                      <Input label="Year Name" required placeholder="e.g. 2026-2027"
                        value={yearForm.name} onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })} />
                      <Input label="Start Date" type="date" required
                        hint="Ages for grades are taken on this date."
                        value={yearForm.startDate} onChange={(e) => setYearForm({ ...yearForm, startDate: e.target.value })} />
                      <Input label="End Date" type="date" required
                        value={yearForm.endDate} onChange={(e) => setYearForm({ ...yearForm, endDate: e.target.value })} />
                    </FormLayout.Grid>
                    {!editKind('year') && (
                      <div style={{ ...sub, marginTop: '6px' }}>A new year becomes the current year; the others are archived.</div>
                    )}
                  </FormLayout>
                  {/* Every year, not just the active one — archived years are the
                      history that class and exam records hang off. */}
                  <TablePanel
                    title="Academic Years"
                       isDarkMode={isDarkMode}
                    minWidth="620px"
                    columns={[
                      { key: 'name', header: 'Academic Year', render: (y) => <strong>{y.name}</strong> },
                      { key: 'startDate', header: 'Start Date', nowrap: true, render: (y) => formatDate(y.startDate) },
                      { key: 'endDate', header: 'End Date', nowrap: true, render: (y) => formatDate(y.endDate) },
                      {
                        key: 'status', header: 'Status', align: 'center',
                        render: (y) => (
                          <Badge tone={y.status === 'Active' ? 'success' : 'neutral'}>{y.status === 'Active' ? 'Current' : 'Archived'}</Badge>
                        )
                      },
                      { key: 'sections', header: 'Sections', align: 'center', render: (y) => classes.items.filter((c) => c.academicYearId === y.id).length },
                    ]}
                    rows={years.items}
                    emptyMessage="No academic years yet."
                  />
                </div>
              )}
              {studentFormOpen && (
                <FormLayout
                  title={editKind('student') ? 'Edit Student' : 'Add a visitor child'}
                  submitLabel={editKind('student') ? 'Update Student' : 'Save Student'}
                  submitting={savingStudent}
                  submitDisabled={noClasses}
                  onSubmit={handleAddStudent}
                  onCancel={() => { setStudentFormOpen(false); setStudentForm(EMPTY_STUDENT); cancelEdit(); }}
                  maxWidth="100%"
                >
                  {!editKind('student') && (
                    <div style={{ ...sub, marginBottom: t.spacing.sm }}>
                      For a child who is <strong>not</strong> in the member directory. Members' children are enrolled with <strong>Enroll from Members</strong>.
                    </div>
                  )}
                  <FormLayout.Grid columns={5}>
                    <Input label="Student Name" required placeholder="Full name"
                      value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} />
                    <Input label="Date of Birth" type="date"
                      value={studentForm.dob} onChange={(e) => setStudentForm({ ...studentForm, dob: e.target.value })} />
                    <Input label="Parent / Guardian" placeholder="Parent name"
                      value={studentForm.parentName} onChange={(e) => setStudentForm({ ...studentForm, parentName: e.target.value })} />
                    <Input label="Phone" placeholder="Contact number"
                      value={studentForm.phone} onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value.replace(/\D/g, '') })} />
                    <Select label="Section" required options={classOptions}
                      value={studentForm.classId} onChange={(e) => setStudentForm({ ...studentForm, classId: e.target.value })} />
                  </FormLayout.Grid>
                </FormLayout>
              )}
              <GradeBrowser
                isDarkMode={isDarkMode}
                notify={notify}
                years={years.items}
                yearScope={yearScope}
                activeYearId={activeYear?.id}
                grades={grades.items}
                sections={classes.items}
                teachers={teachers.items}
                students={students}
                registers={weekly.items}
                onCreateGrades={createGrades}
                onUpdateGrade={grades.update}
                onVoidGrade={(g) => setDeleting({ kind: 'grade', rawId: g.rawId as number, label: `grade ${g.name}` })}
                onAddSections={classes.addSections}
                onUpdateSection={classes.update}
                onVoidSection={(c) => setDeleting({ kind: 'section', rawId: c.rawId as number, label: `section ${classNameOf(c.id)}` })}
                onEnroll={enrollStudents}
                onMoveStudent={moveStudent}
                onRemoveStudent={(st) => setDeleting({ kind: 'student', rawId: rawStudents.find((r) => r.studentId === st.id)?.id, label: `student ${st.name}` })}
                onAddVisitor={addVisitorTo}
                onEditStudent={startEditStudent}
              />
            </>
          )}

          {classesSubTab === 'teachers' && (
            <div ref={teacherFormRef}>
              {noClasses && noClassesWarning}
              <FormLayout
                title={editKind('teacher') ? 'Edit Teacher' : 'Add Teacher'}
                submitLabel={editKind('teacher') ? 'Update Teacher' : 'Save Teacher'}
                submitDisabled={noClasses}
                onSubmit={handleAddTeacher}
                onCancel={editKind('teacher') ? cancelEdit : undefined}
                maxWidth="100%"
              >
                <FormLayout.Grid columns={4}>
                  <Input label="Teacher Name" required placeholder="Full name"
                    value={teacherForm.name} onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })} />
                  <Input label="Phone" placeholder="Mobile number"
                    value={teacherForm.phone} onChange={(e) => setTeacherForm({ ...teacherForm, phone: e.target.value.replace(/\D/g, '') })} />
                  <Input label="Email" type="email" placeholder="Email ID"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value.toLowerCase() })} />
                  <Select label="Section" required options={classOptions}
                    hint="Teaches two sections? Save this teacher once per section."
                    value={teacherForm.classId} onChange={(e) => setTeacherForm({ ...teacherForm, classId: e.target.value })} />
                </FormLayout.Grid>
              </FormLayout>

              {loginFor && (
                <form onSubmit={submitCreateLogin}>
                  <div style={{ ...panel, borderLeft: `4px solid ${t.info}`, marginTop: t.spacing.md }}>
                    <div style={styles.sectionTitle}>Create login for {loginFor.name}</div>
                    <div style={{ ...sub, marginBottom: t.spacing.sm }}>
                      They will sign in with this and see only their own section(s): mark attendance, note offerings, enter marks, print report cards. Nothing else in the app.
                    </div>
                    <FormLayout.Grid columns={3}>
                      <Input label="Username" required placeholder="mary.thomas"
                        value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} />
                      {/* Eye toggle so a typed password can be checked before
                          it is handed to the teacher (CHM-136). */}
                      <div style={{ position: 'relative' }}>
                        <Input label="Password" type={showPassword ? 'text' : 'password'} required placeholder="At least 6 characters"
                          value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          title={showPassword ? 'Hide password' : 'Show password'}
                          style={{
                            position: 'absolute', right: '10px', bottom: '9px',
                            background: 'none', border: 'none', padding: 0,
                            cursor: 'pointer', fontSize: t.fontSize.md, lineHeight: 1
                          }}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </FormLayout.Grid>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '8px' }}>
                      <Button type="button" variant="secondary" onClick={() => setLoginFor(null)}>Cancel</Button>
                      <Button type="submit" disabled={savingLogin}>{savingLogin ? 'Creating…' : 'Create login'}</Button>
                    </div>
                  </div>
                </form>
              )}

              <div style={{ marginTop: t.spacing.lg }}>
                <TablePanel
                  title={<>Teachers — {scopeName}</>}
                  above={(
                    <SearchBar
                      value={teacherSearch.searchTerm}
                      onChange={teacherSearch.setSearchTerm}
                      onClear={teacherSearch.clear}
                      placeholder="Search by name, phone, email or section..."
                    />
                  )}
                     isDarkMode={isDarkMode}
                  minWidth="420px"
                  columns={[
                    { key: 'name', header: 'Teacher', render: (x) => <strong>{x.name}</strong> },
                    { key: 'classId', header: 'Section', render: (x) => classNameOf(x.classId) },
                    { key: 'phone', header: 'Phone', render: (x) => <>{x.phone || '-'}<br /><span style={sub}>{x.email || ''}</span></> },
                    {
                      key: 'username', header: 'Login',
                      render: (x) => x.username
                        ? <Badge tone="success">🔑 {x.username}</Badge>
                        : <span style={sub}>none</span>
                    },
                    {
                      key: 'actions', header: '', align: 'right',
                      render: (x) => (
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                          <Button write variant="ghost" size="sm" onClick={() => startEditTeacher(x)}>Edit</Button>
                          {!x.username && <Button write size="sm" variant="secondary" onClick={() => openCreateLogin(x)}>Create login</Button>}
                        </div>
                      )
                    }
                  ]}
                  rows={teacherSearch.filtered}
                  emptyMessage="No teachers yet."
                />
              </div>
            </div>
          )}

        </>
      )}

      {/* ================================================== 📝 EXAMS */}
      {activeTab === 'exams' && (
        <>
          {noClasses && noClassesWarning}
          <SubTabs
            value={examSubTab}
            onChange={(k) => { setExamSubTab(k); closeMarks(); }}
            tabs={[
              ...(teacherLogin ? [] : [{ key: 'create', label: 'Exams' }]),
              { key: 'marks', label: marksSectionId ? '✎ Entering marks…' : 'Enter Marks' },
              { key: 'reports', label: 'Report Cards' }
            ]}
          />

          {/* ---- Exams: create several at once; one list with progress ---- */}
          {examSubTab === 'create' && !teacherLogin && (
            <>

              {examFormOpen && editKind('exam') && (
                <FormLayout
                  title={`Edit ${examEdit.name}`}
                  submitLabel="Update Exam"
                  onSubmit={submitExamEdit}
                  onCancel={() => { setExamFormOpen(false); cancelEdit(); }}
                  maxWidth="100%"
                >
                  <FormLayout.Grid columns={6}>
                    <Input label="Exam Name" required value={examEdit.name} onChange={(e) => setExamEdit({ ...examEdit, name: e.target.value })} />
                    <Input label="Subject" placeholder="e.g. Bible Knowledge" value={examEdit.subject} onChange={(e) => setExamEdit({ ...examEdit, subject: e.target.value })} />
                    <Input label="Date" type="date" required value={examEdit.date} onChange={(e) => setExamEdit({ ...examEdit, date: e.target.value })} />
                    <Input label="Total Marks" type="number" min="1" required value={examEdit.totalMarks} onChange={(e) => setExamEdit({ ...examEdit, totalMarks: e.target.value })} />
                    <Input label="Pass Marks" type="number" min="0" required hint="Marks needed to pass."
                      value={examEdit.passMarks} onChange={(e) => setExamEdit({ ...examEdit, passMarks: e.target.value })} />
                    {examEdit.classId ? (
                      <Input label="For" disabled value={classNameOf(examEdit.classId)} hint="An older exam set up for one section." />
                    ) : (
                      <Select label="For" placeholder="All grades"
                        options={grades.items.filter((g) => g.academicYearId === examEdit.academicYearId).map((g) => ({ value: g.id as string, label: g.name as string }))}
                        value={examEdit.gradeId} onChange={(e) => setExamEdit({ ...examEdit, gradeId: e.target.value })} />
                    )}
                  </FormLayout.Grid>
                </FormLayout>
              )}

              {examFormOpen && !editKind('exam') && (
                <FormLayout
                  title="Create Exams"
                  submitLabel={savingExams ? 'Creating…' : `Create ${examRows.filter((r) => r.name.trim()).length || ''} Exam${examRows.filter((r) => r.name.trim()).length === 1 ? '' : 's'}`}
                  submitting={savingExams}
                  onSubmit={submitExamRows}
                  onCancel={() => setExamFormOpen(false)}
                  maxWidth="100%"
                >
                  <div style={{ marginBottom: t.spacing.md, maxWidth: '260px' }}>
                    <Select label="Academic Year (for every row)" required containerStyle={{ margin: 0 }}
                      options={years.items.map((y) => ({ value: y.id, label: `${y.name}${y.id === activeYear?.id ? ' (current)' : ''}` }))}
                      value={examYear} onChange={(e) => setExamYear(e.target.value)} />
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                      <thead>
                        <tr style={{ ...sub, textAlign: 'left' }}>
                          <th style={{ padding: '4px 6px', width: '32px' }}>#</th>
                          <th style={{ padding: '4px 6px' }}>Exam Name</th>
                          <th style={{ padding: '4px 6px' }}>Subject</th>
                          <th style={{ padding: '4px 6px', width: '170px' }}>Date</th>
                          <th style={{ padding: '4px 6px', width: '110px' }}>Total Marks</th>
                          <th style={{ padding: '4px 6px', width: '110px' }}>Pass Marks</th>
                          <th style={{ padding: '4px 6px', width: '200px' }}>For</th>
                          <th style={{ padding: '4px 6px', width: '60px' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {examRows.map((r, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${t.borderDashed}` }}>
                            <td style={{ padding: '8px 6px', color: t.textFaint }}>{i + 1}</td>
                            <td style={{ padding: '4px 6px' }}>
                              <Input placeholder={i === 0 ? 'e.g. Half Yearly' : 'e.g. Annual'} containerStyle={{ margin: 0 }} value={r.name} onChange={(e) => setExamRow(i, { name: e.target.value })} />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <Input placeholder={i === 0 ? 'e.g. Bible Knowledge' : 'e.g. Memory Verses'} containerStyle={{ margin: 0 }} value={r.subject} onChange={(e) => setExamRow(i, { subject: e.target.value })} />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <Input type="date" containerStyle={{ margin: 0 }} value={r.date} onChange={(e) => setExamRow(i, { date: e.target.value })} />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <Input type="number" min="1" containerStyle={{ margin: 0 }} value={r.totalMarks}
                                // The pass mark follows the total at 40% until it has been typed by hand.
                                onChange={(e) => setExamRow(i, { totalMarks: e.target.value, ...(r.passTouched ? {} : { passMarks: defaultPass(e.target.value) }) })} />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <Input type="number" min="0" containerStyle={{ margin: 0 }} value={r.passMarks}
                                onChange={(e) => setExamRow(i, { passMarks: e.target.value, passTouched: true })} />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <Select placeholder="All grades" containerStyle={{ margin: 0 }}
                                options={grades.items.filter((g) => g.academicYearId === examYear).map((g) => ({ value: g.id as string, label: g.name as string }))}
                                value={r.gradeId} onChange={(e) => setExamRow(i, { gradeId: e.target.value })} />
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <Button type="button" variant="ghost" size="sm" title="Remove row"
                                onClick={() => setExamRows((prev) => (prev.length === 1 ? [{ ...EMPTY_EXAM_ROW }] : prev.filter((_, j) => j !== i)))}>✕</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '8px', flexWrap: 'wrap' }}>
                    <Button type="button" variant="secondary" size="sm"
                      onClick={() => setExamRows((prev) => [...prev, { ...EMPTY_EXAM_ROW, name: prev[prev.length - 1]?.name || '', date: prev[prev.length - 1]?.date || '', totalMarks: prev[prev.length - 1]?.totalMarks || '100', passMarks: prev[prev.length - 1]?.passMarks || '40' }])}>
                      + Add another exam / subject
                    </Button>
                    <span style={sub}>"All grades" means every section in the year sits it. Marks are entered per section afterwards.</span>
                  </div>
                </FormLayout>
              )}

              <TablePanel
                title={<>Exams — {scopeName}</>}
                actions={!examFormOpen && <Button write size="sm" onClick={openCreateExams} disabled={noClasses}>+ Create Exams</Button>}
                   isDarkMode={isDarkMode}
                minWidth="640px"
                columns={[
                  { key: 'name', header: 'Exam', render: (x) => <strong>{x.name}</strong> },
                  { key: 'subject', header: 'Subject', render: (x) => x.subject || <span style={sub}>—</span> },
                  { key: 'date', header: 'Date', nowrap: true, render: (x) => formatDate(x.date) },
                  { key: 'scope', header: 'For', render: (x) => examScopeLabel(x) },
                  { key: 'totalMarks', header: 'Marks', align: 'center', nowrap: true, render: (x) => <>{x.totalMarks}<br /><span style={sub}>pass {passMarkFor(x)}</span></> },
                  {
                    key: 'passRate', header: 'Pass Rate', align: 'center',
                    render: (x) => {
                      const p = examProgress(x);
                      return p.passPct === null
                        ? <span style={sub}>—</span>
                        : <><Badge tone={p.passPct >= 75 ? 'success' : p.passPct >= 50 ? 'warning' : 'error'}>{p.passPct}%</Badge><br /><span style={sub}>{p.passed} of {p.sat} passed</span></>;
                    }
                  },
                  {
                    key: 'progress', header: 'Marks Entered', align: 'center',
                    render: (x) => {
                      const p = examProgress(x);
                      return p.sections === 0
                        ? <span style={sub}>no sections</span>
                        : <Badge tone={p.done === p.sections ? 'success' : p.done ? 'warning' : 'neutral'}>{p.done} / {p.sections} sections</Badge>;
                    }
                  },
                  {
                    key: 'actions', header: '', align: 'right',
                    render: (x) => (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                        <Button write variant="ghost" size="sm" onClick={() => { setSelectedExamId(x.id); setExamSubTab('marks'); }}>Enter marks</Button>
                        <Button write variant="ghost" size="sm" onClick={() => startEditExam(x)}>Edit</Button>
                        <Button variant="danger" size="sm" style={styles.deleteBtn} disabled={examProgress(x).done > 0}
                          title={examProgress(x).done > 0 ? 'Marks have been entered for this exam' : undefined}
                          onClick={() => setDeleting({ kind: 'exam', rawId: x.rawId as number, label: `exam ${x.name}` })}>Delete</Button>
                      </div>
                    )
                  }
                ]}
                rows={scopedExams}
                emptyMessage="No exams yet. Create them once for the whole school."
              />
            </>
          )}

          {/* ---- Enter marks: exam -> section list -> students ---- */}
          {examSubTab === 'marks' && !marksSectionId && (
            <>
              <div style={panel}>
                <Select
                  label="Exam"
                  placeholder="Pick an exam…"
                  options={scopedExams.map((x) => ({ value: x.id, label: `${examTitle(x)} · ${formatDate(x.date)} · ${examScopeLabel(x)} · out of ${x.totalMarks}` }))}
                  value={selectedExamId}
                  onChange={(e) => setSelectedExamId(e.target.value)}
                />
              </div>
              {selectedExam && (
                <TablePanel
                  title={<>{examTitle(selectedExam)} — sections</>}
                     isDarkMode={isDarkMode}
                  minWidth="600px"
                  columns={[
                    { key: 'label', header: 'Section', render: (r) => <strong>{r.label}</strong> },
                    { key: 'students', header: 'Students', align: 'center' },
                    {
                      key: 'status', header: 'Marks',
                      render: (r) => r.entered > 0
                        ? <><Badge tone={r.entered >= r.students ? 'success' : 'warning'}>✔ Entered</Badge> <span style={{ color: t.textMain }}>{r.entered} of {r.students}</span></>
                        : <Badge tone="neutral">Not entered</Badge>
                    },
                    {
                      key: 'result', header: 'Result',
                      render: (r) => r.tally
                        ? <><span style={{ color: t.income, fontWeight: 'bold' }}>{r.tally.passed} passed</span>{r.tally.failed ? <span style={{ color: t.danger }}> · {r.tally.failed} failed</span> : null}{r.tally.absent ? <span style={sub}> · {r.tally.absent} absent</span> : null}</>
                        : <span style={sub}>—</span>
                    },
                    {
                      key: 'actions', header: '', align: 'right',
                      render: (r) => (
                        <Button write size="sm" variant={r.entered > 0 ? 'ghost' : 'primary'} disabled={r.students === 0}
                          title={r.students === 0 ? 'No students in this section' : undefined}
                          onClick={() => openMarks(r.id)}>{r.entered > 0 ? 'Edit' : 'Enter marks'}</Button>
                      )
                    }
                  ]}
                  rows={examSectionRows}
                  rowKey="id"
                  emptyMessage="No sections sit this exam."
                />
              )}
            </>
          )}

          {examSubTab === 'marks' && marksSectionId && selectedExam && (
            <>
              <div style={editingBanner}>
                <strong>{examTitle(selectedExam)}</strong> · {classNameOf(marksSectionId)} · out of {selectedExam.totalMarks} · pass mark {passMarkFor(selectedExam)}
                <Button type="button" size="sm" variant="ghost" style={{ marginLeft: '12px' }} onClick={closeMarks}>← Back to sections</Button>
              </div>
              <div style={panel}>
                {marksStudents.length === 0 ? (
                  <div style={{ color: t.textFaint, fontStyle: 'italic', padding: '10px' }}>
                    No students in this section.
                  </div>
                ) : (
                  marksStudents.map((s) => {
                    const problem = markProblem(marksMap[s.id]?.obtained, selectedExam.totalMarks);
                    return (
                      <div key={s.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', padding: '8px 0', borderBottom: `1px solid ${t.borderDashed}` }}>
                        <div style={{ flex: 2, color: t.textMain, fontWeight: 'bold' }}>
                          {s.name}<div style={sub}>{s.studentId}</div>
                        </div>
                        <div style={{ width: '70px', paddingBottom: '10px' }}>
                          {(() => { const r = resultOf(marksMap[s.id]?.obtained, selectedExam); return r === 'ABSENT' ? <span style={sub}>absent</span> : <Badge tone={r === 'PASS' ? 'success' : 'error'}>{r === 'PASS' ? 'Pass' : 'Fail'}</Badge>; })()}
                        </div>
                        <Input
                          label="Marks" type="number" min="0" max={selectedExam.totalMarks} containerStyle={{ flex: 1 }}
                          error={problem ? `Mark ${problem}` : undefined}
                          value={marksMap[s.id]?.obtained ?? ''}
                          onChange={(e) => setMarksMap((prev) => ({
                            ...prev, [s.id]: { ...(prev[s.id] || {}), obtained: e.target.value }
                          }))}
                        />
                        <Input
                          label="Remarks" containerStyle={{ flex: 2 }}
                          value={marksMap[s.id]?.remarks ?? ''}
                          onChange={(e) => setMarksMap((prev) => ({
                            ...prev, [s.id]: { ...(prev[s.id] || {}), remarks: e.target.value }
                          }))}
                        />
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                <Button type="button" variant="secondary" onClick={closeMarks}>Cancel</Button>
                <Button write onClick={handleSaveMarks}>Save Marks</Button>
              </div>
            </>
          )}

          {/* ---- Report cards: section -> students -> print ---- */}
          {examSubTab === 'reports' && (
            <>
              <div style={panel}>
                <Select
                  label="Section"
                  placeholder="Pick a section…"
                  options={classOptions}
                  value={reportSectionId}
                  onChange={(e) => setReportSectionId(e.target.value)}
                />
              </div>
              {reportSectionId && (
                <TablePanel
                  title={<>Results — {classNameOf(reportSectionId)}</>}
                  actions={(
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {([['all', 'All'], ['failed', 'Failed any'], ['none', 'No marks']] as const).map(([k, label]) => (
                        <Button key={k} size="sm" variant={resultFilter === k ? 'primary' : 'ghost'} onClick={() => setResultFilter(k)}>{label}</Button>
                      ))}
                    </div>
                  )}
                  above={(
                    <div style={{ ...sub, marginBottom: t.spacing.sm }}>
                      {reportStudents.filter((r) => r.exams > 0).length} with marks · <strong style={{ color: t.income }}>{reportStudents.filter((r) => r.exams > 0 && r.failed === 0).length} passed every exam</strong> · <span style={{ color: t.danger }}>{reportStudents.filter((r) => r.failed > 0).length} failed at least one</span>
                    </div>
                  )}
                     isDarkMode={isDarkMode}
                  minWidth="600px"
                  columns={[
                    { key: 'name', header: 'Student', render: (r) => <><strong>{r.name}</strong><br /><span style={sub}>{r.studentId}</span></> },
                    { key: 'exams', header: 'Exams Sat', align: 'center', render: (r) => r.exams || <span style={sub}>none</span> },
                    {
                      key: 'result', header: 'Result', align: 'center',
                      render: (r) => r.exams === 0
                        ? <span style={sub}>—</span>
                        : r.failed === 0
                          ? <Badge tone="success">Passed {r.passed}/{r.exams}</Badge>
                          : <Badge tone="error">Failed {r.failed} of {r.exams}</Badge>
                    },
                    {
                      key: 'pct', header: 'Overall', align: 'center',
                      render: (r) => r.pct === null
                        ? <span style={sub}>—</span>
                        : <Badge tone={r.pct >= 60 ? 'success' : r.pct >= 40 ? 'warning' : 'error'}>{r.pct.toFixed(1)}%</Badge>
                    },
                    {
                      key: 'card', header: '', align: 'right', nowrap: true,
                      render: (r) => (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Button size="sm" variant="ghost" title="View the report card" onClick={() => setCardStudentId(r.id)}>👁️ View card</Button>
                          <PrintButton
                            module="Sunday School"
                            documentType="REPORT_CARD"
                            size="sm"
                            variant="ghost"
                            label="🖨️ Report card"
                            title={`ReportCard-${r.name}`}
                            data={() => reportCardData(r)}
                            onNotify={notify}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )
                    }
                  ]}
                  rows={reportStudents.filter((r) => (resultFilter === 'all' ? true : resultFilter === 'failed' ? r.failed > 0 : r.exams === 0))}
                  rowKey="id"
                  emptyMessage="No students in this section."
                />
              )}
              {cardStudentId && (() => {
                const student = students.find((s) => s.id === cardStudentId);
                if (!student) return null;
                const card = reportCardData(student);
                const rows = reportRowsFor(student.id);
                const obtained = rows.reduce((n, x) => n + x.obtained, 0);
                const total = rows.reduce((n, x) => n + x.total, 0);
                return (
                  <Modal
                    open
                    title={`Report Card — ${student.name}`}
                    onClose={() => setCardStudentId(null)}
                    width="820px"
                    isDarkMode={isDarkMode}
                    footer={(
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button variant="secondary" onClick={() => setCardStudentId(null)}>Close</Button>
                        <PrintButton
                          module="Sunday School"
                          documentType="REPORT_CARD"
                          label="🖨️ Print report card"
                          title={`ReportCard-${student.name}`}
                          data={() => card}
                          onNotify={notify}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    )}
                  >
                    <div style={{ ...sub, marginBottom: t.spacing.md }}>
                      {student.studentId} · {card.className} · {card.academicYear || '-'}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                        <thead>
                          <tr style={{ ...sub, textAlign: 'left' }}>
                            {['#', 'Exam', 'Subject', 'Date', 'Marks', '%', 'Result', 'Remarks'].map((h) => (
                              <th key={h} style={{ padding: '6px', borderBottom: `2px solid ${t.borderStrong}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 && (
                            <tr><td colSpan={8} style={{ padding: '12px 6px', color: t.textFaint }}>No marks entered for this student yet.</td></tr>
                          )}
                          {rows.map((x, i) => (
                            <tr key={x.id as string} style={{ borderTop: `1px solid ${t.border}` }}>
                              <td style={{ padding: '6px', color: t.textFaint }}>{i + 1}</td>
                              <td style={{ padding: '6px' }}><strong>{x.exam}</strong></td>
                              <td style={{ padding: '6px' }}>{x.subject || <span style={sub}>—</span>}</td>
                              <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{formatDate(x.date)}</td>
                              <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{x.obtained} / {x.total}</td>
                              <td style={{ padding: '6px' }}>{x.pct.toFixed(1)}%</td>
                              <td style={{ padding: '6px' }}><Badge tone={x.result === 'PASS' ? 'success' : 'error'}>{x.result}</Badge></td>
                              <td style={{ padding: '6px' }}>{x.remarks || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                        {rows.length > 0 && (
                          <tfoot>
                            <tr style={{ borderTop: `2px solid ${t.borderStrong}`, fontWeight: 'bold' }}>
                              <td colSpan={4} style={{ padding: '6px' }}>Overall</td>
                              <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{obtained} / {total}</td>
                              <td style={{ padding: '6px' }}>{total > 0 ? `${((obtained / total) * 100).toFixed(1)}%` : '—'}</td>
                              <td colSpan={2} style={{ padding: '6px' }}>
                                Passed {rows.filter((x) => x.result === 'PASS').length} of {rows.length}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </Modal>
                );
              })()}
            </>
          )}
        </>
      )}

      {/* ================================================== 📊 SUMMARY */}
      {activeTab === 'summary' && (
        <SundaySchoolSummary
          sections={summarySections}
          students={scopedStudents}
          teachers={scopedTeachers}
          registers={scopedWeekly}
          offerings={scopedOfferings}
          exams={scopedExams}
          marks={marks.items}
          term={summaryTerm}
          scopeName={scopeName}
          unassigned={unassignedStudents}
          loading={loading}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default SundaySchool;
